import {
  useModals,
  Modal,
  ModalProps,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  SelectItem,
  Stack,
  Text,
} from "@skailer-com/uikit";
import { TimeOffBreakdownCustomAmountModal } from "@/pages/settings/time-off/policy/widget/TimeOffBreakdownCustomAmountModal.tsx";
import { ApiCalendarHolidayDateType } from "@/types/calendar-holiday.types.ts";
import { ApiTimeOffPeriodTrackingTime } from "@/types/time-off/time-off-policy.types.ts";
import { ApiTimeOffBreakdown, ApiTimeOffBreakdownItem } from "@/types/time-off/time-off.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";

import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { z } from "zod";

export interface TimeOffBreakdownModalProps extends ModalProps {
  breakdown: ApiTimeOffBreakdown;
  available_balance: number;
  period_track_type?: ApiTimeOffPeriodTrackingTime;
  onApply?: (updated: ApiTimeOffBreakdown) => void;
  viewMode?: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const breakdownSchema = z.object({
  items: z.array(
    z.object({
      date: z.date(),
      amount: z.number(),
      min_amount: z.number(),
      max_amount: z.number(),
      unit_type: z.enum(ApiTimeOffPeriodTrackingTime),
      type_date: z.enum(ApiCalendarHolidayDateType),
    })
  ),
  total_amount: z.number(),
  is_manual_modified: z.boolean(),
});

type BreakdownSchemaType = z.infer<typeof breakdownSchema>;

export const TimeOffBreakdownModal = ({
  breakdown,
  period_track_type,
  available_balance,
  onApply,
  viewMode,
  ...props
}: TimeOffBreakdownModalProps) => {
  const { close, openModal } = useModals();
  const { id } = { ...props };
  const { t, i18n } = useTranslation();
  const isView = viewMode === true;

  const { control, handleSubmit, watch, setValue } = useForm<BreakdownSchemaType>({
    mode: "onSubmit",
    resolver: zodResolver(breakdownSchema),
    defaultValues: {
      items: breakdown.items.map((i) => ({
        ...i,
        date: new Date(i.date),
        amount: Number(i.amount),
        min_amount: Number(i.min_amount),
        max_amount: Number(i.max_amount),
      })),
      total_amount: Number(breakdown.total_amount),
      is_manual_modified: breakdown.is_manual_modified,
    },
    disabled: isView,
  });

  const items = watch("items");

  const updateItemAmount = (index: number, amount: number) => {
    const updated = [...items];
    updated[index].amount = amount;
    setValue("items", updated, { shouldDirty: true });

    const total = updated.reduce((s, i) => s + i.amount, 0);
    setValue("total_amount", total);
    setValue("is_manual_modified", true);
  };

  const openCustomAmount = (index: number) => {
    openModal(TimeOffBreakdownCustomAmountModal, {
      items,
      index,
      period_track_type,
      onApply: ({ amount, applyToAll }) => {
        let updated;

        if (applyToAll) {
          updated = items.map((it) =>
            it.type_date === ApiCalendarHolidayDateType.non_working ? it : { ...it, amount }
          );
        } else {
          updated = [...items];
          updated[index].amount = amount;
        }

        setValue("items", updated, { shouldDirty: true });

        const total = updated.reduce((s, i) => s + i.amount, 0);
        setValue("total_amount", total);
        setValue("is_manual_modified", true);
      },
    });
  };

  const total = Number(watch("total_amount") ?? 0);

  const getTimeOptions = (item: ApiTimeOffBreakdownItem): SelectItem[] => {
    const isHours = item.unit_type === ApiTimeOffPeriodTrackingTime.hours;
    const max = Number(item.max_amount);
    const min = Number(item.min_amount);

    const baseValues = isHours ? [8, 4, 2, 1, 0] : [1, 0.5, 0];

    let values = baseValues.filter((v) => v <= max && v >= min);

    if (!values.includes(min)) values.push(min);
    if (!values.includes(max)) values.unshift(max);

    values = Array.from(new Set(values)).sort((a, b) => b - a);

    return [
      ...values.map((v) => ({
        id: String(v),
        title:
          `${t(pluralKey, {
            count: parseFloat(String(v)),
          })}` +
          (v === max
            ? ` (${t("time_off:full_day")})`
            : v * 2 === max
              ? ` (${t("time_off:half_day")})`
              : ""),
      })),
      {
        id: "custom",
        title: `${t("time_off:custom_amount")}...`,
        caption: t("time_off:custom_amount_caption"),
      },
    ];
  };

  const submit = (schema: BreakdownSchemaType) => {
    if (onApply) {
      onApply(schema);
    }
    close(id);
  };

  const isNonWorking = (type: ApiCalendarHolidayDateType) =>
    type === ApiCalendarHolidayDateType.non_working;

  const isHours = period_track_type === ApiTimeOffPeriodTrackingTime.hours;
  const pluralKey = isHours ? "plurals:plural_hours" : "plurals:plural_day";

  return (
    <Modal
      {...props}
      id={id}
      title={isView ? t("time_off:breakdown") : t("time_off:customize_breakdown")}
      caption={
        isView
          ? `${t("common:total")} ${t(pluralKey, {
              count: breakdown.total_amount,
            })}` +
            (isHours && breakdown.items.length > 0
              ? ` (${t("time_off:in_units")} ${t("plurals:plural_day", {
                  count: breakdown.items.length,
                })})`
              : "")
          : undefined
      }
      withCloser
      actions={
        <Stack gap={"sm"}>
          <Button size="lg" className={"w-full"} onClick={handleSubmit(submit)}>
            {isView
              ? t("common:ok")
              : `${t("common:apply")} ${t(pluralKey, {
                  count: total,
                })}`}
          </Button>
          {available_balance > 0 && (
            <Text className="text-secondary text-center">
              {`${t("time_off:available_balance")} ${t(pluralKey, {
                count: available_balance,
              })}`}
            </Text>
          )}
        </Stack>
      }
    >
      <Controller
        control={control}
        name="items"
        render={() => (
          <Stack>
            {items.map((item, index) => {
              return (
                <ListItem
                  key={`${item.date.toISOString()}-${index}`}
                  titleSlot={
                    <Text
                      className={`font-semibold ${isNonWorking(item.type_date) ? "text-danger" : ""}`}
                    >
                      {item.date.toLocaleDateString(i18n.language, {
                        month: "short",
                        day: "numeric",
                      })}
                    </Text>
                  }
                  caption={item.date.toLocaleDateString(i18n.language, {
                    weekday: "long",
                  })}
                  valueSlot={
                    !isNonWorking(item.type_date) ? (
                      isView ? (
                        <Text>{Number(item.amount).toFixed(1)}</Text>
                      ) : (
                        <DropdownMenu
                          trigger={
                            <Button
                              variant="menu"
                              rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
                            >
                              {Number(item.amount).toFixed(1)}
                            </Button>
                          }
                        >
                          {getTimeOptions(item).map((opt) => (
                            <DropdownMenuItem
                              key={opt.id}
                              title={opt.title}
                              description={opt.caption}
                              onClick={() => {
                                if (isView) return;
                                if (opt.id === "custom") {
                                  openCustomAmount(index);
                                } else {
                                  updateItemAmount(index, Number(opt.id));
                                }
                              }}
                            />
                          ))}
                        </DropdownMenu>
                      )
                    ) : (
                      <Text>{t("time_off:not_available")}</Text>
                    )
                  }
                />
              );
            })}
          </Stack>
        )}
      />
    </Modal>
  );
};
